import React from "react";
import Page from "./components/Page";
import Catalog from "./routes/Catalog";
import Calendar from "./routes/Calendar";
import Upcoming from "./routes/Upcoming/UpcomingContainer";
import EventDetail from "./routes/EventDetail/EventDetailContainer";
import SongDetail from "./routes/SongDetail/SongDetailContainer";
import SongCreate from "./routes/SongDetail/SongCreateContainer";
import ShareTarget from "./routes/ShareTarget";
import SongEdit from "./routes/SongDetail/SongEditContainer";
import { Login } from "./routes/Login";
import { NotInvited } from "./routes/NotInvited";
import { NotFound } from "./routes/NotFound";
import { EmailInUse } from "./routes/EmailInUse";
import { VerifyToLink } from "./routes/VerifyToLink";
import { ConfirmErasure } from "./routes/ConfirmErasure";
import { VerifyTeamRequest } from "./routes/VerifyTeamRequest";
import { ConfirmEmail } from "./routes/ConfirmEmail";
import EmailConfirmationBanner from "./components/EmailConfirmationBanner";
import AppTour from "./components/AppTour";
import { NoTeam } from "./routes/NoTeam";
import { PrivacyPolicy } from "./routes/PrivacyPolicy";
import { TermsOfService } from "./routes/TermsOfService";
import { Home } from "./routes/Home";
import { Signup } from "./routes/Signup";
import { RequestAccess } from "./routes/RequestAccess";
import Admin from "./routes/Admin";
import SetlistTemplates from "./routes/SetlistTemplates/SetlistTemplatesContainer";
import AccountSettings from "./routes/AccountSettings"
import PasswordReset from "./routes/PasswordReset";
import Navbar from "./components/Navigationbar";
import InstallBanner from "./components/InstallBanner";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
  useLocation,
} from "react-router-dom";
import CssBaseline from "@material-ui/core/CssBaseline";
import {Box, CircularProgress} from "@material-ui/core";
import { MuiThemeProvider, createTheme } from "@material-ui/core/styles";
import SongProposals from "./routes/SongProposals";
import { UserContext } from "./contexts/UserContext";
import { ThemeContext } from "./contexts/ThemeContext";
import { UpcomingEventsContext } from "./contexts/UpcomingEventsContext";
import { SongProposalsContext } from "./contexts/SongProposalsContext";
import useUpcomingEvents from "./hooks/useUpcomingEvents";
import useSongProposals from "./hooks/useSongProposals";
import { TOKEN_KEY, USER_KEY } from "./CONSTANTS";
import { buildUser, loggedOutUser } from "./utils/user";
import axios from "axios";
import { SIGNED_OUT_EVENT, refreshAccessToken } from "restApi/axiosConfig";
import { reportMilestone } from "utils/milestones";
import { isStandalone } from "utils/installBanner";

// Defined at module scope (not inside App) so its identity never
// changes across renders - React remounts a whole subtree whenever a
// component's function reference changes between renders, and App
// re-renders often (theme toggles, the auth check, and - critically -
// every tick of useUpcomingEvents' polling and every markSeen() call
// while sitting on /upcoming). A PrivateRoute redefined inline would
// remount Navbar (and the current page) on every one of those, which
// is exactly what was flashing Navigationbar's mobile layout back in
// after Upcoming's data loaded: useMediaQuery's breakpoint state
// resets on mount and only corrects itself a moment later, and repeated
// remounts kept resetting it faster than it could stabilize.
const PrivateRoute = ({ children, ...rest }) => {
  // Remember where the user was headed so Login can send them back
  // there after a successful sign-in, instead of always dropping
  // them on /calendar.
  const location = useLocation();
  const { user } = React.useContext(UserContext);

  if (user.isAuthenticated) {
    // Everything but Account is scoped to the active team, so someone with
    // no team (left, removed, last team gone) gets the "join a team" page
    // instead of screens that never finish loading. Account stays reachable
    // (erasing your data, linked sign-ins, leaving); staff can switch into
    // any team without a membership, so they keep the normal pages.
    const needsTeam = !user.team && !user.is_staff && rest.path !== "/account";
    return (
      <Box>
        <Navbar />
        {needsTeam ? <NoTeam /> : <Route {...rest}>{children}</Route>}
      </Box>
    );
  }
  // No cleanup needed here: every real path to isAuthenticated becoming
  // false already clears localStorage/state on its own - the explicit
  // sign-out handler (SettingsMenu.jsx), the auth-check effect's catch
  // below (an invalid/expired token), axiosConfig.js's response
  // interceptor (a token that fails verification mid-session, hard-
  // redirecting via window.location.href), or there was never a token
  // to begin with (a fresh, never-logged-in visit). Calling setUser()
  // here too would update App's state while PrivateRoute is rendering,
  // which React disallows ("Cannot update a component while rendering
  // a different component").
  return <Redirect to={{ pathname: "/login", state: { from: location } }} />;
};

function App() {
  const [themePalette, setThemePalette] = React.useState("dark");
  const theme = createTheme({
    palette: {
      type: themePalette,
    },
    overrides: {
      MuiOutlinedInput: {
        input: {
          "&:-webkit-autofill": {
            WebkitBoxShadow:
              themePalette === "dark" ? "0 0 0 100px #4a4e54 inset" : "#00ff00",
            WebkitTextFillColor: "#fff",
          },
        },
      },
      // Without this, a page that's shorter than the viewport (no
      // scrollbar) and then grows past it once its data loads in
      // (scrollbar appears) loses a few pixels of width right at that
      // moment - on a browser window sized close to a breakpoint (e.g.
      // MUI's 960px "md"), that alone is enough to cross it, flipping
      // Navigationbar's desktop tabs into its mobile bottom-nav layout
      // right after content loads. Reserving the gutter unconditionally
      // keeps viewport width - and any width-based breakpoint - stable
      // no matter how tall the content ends up being.
      MuiCssBaseline: {
        "@global": {
          html: {
            overflowY: "scroll",
          },
        },
      },
    },
  });
  const storageUser = JSON.parse(localStorage.getItem("user"));
  const [config, setConfig] = React.useState(null);

  const [user, setUser] = React.useState(
    storageUser ? buildUser(storageUser, storageUser.isAuthenticated) : loggedOutUser()
  );
  const userContextProviderValue = React.useMemo(() => ({ user, setUser }), [
    user,
    setUser,
  ]);
  const themeContextProviderValue = React.useMemo(
    () => ({ themePalette, setThemePalette }),
    [themePalette, setThemePalette]
  );
  const upcomingEventsContextProviderValue = useUpcomingEvents(
    user.isAuthenticated
  );
  const songProposalsContextProviderValue = useSongProposals(
    user.isAuthenticated
  );

  // `user.isAuthenticated` above is just whatever was last written to
  // localStorage - it doesn't mean the token is still actually valid.
  // Without this, a stale-but-"authenticated" localStorage (e.g. an
  // expired session) would let PrivateRoute render the real protected
  // page immediately, only bouncing to /login after an API call fails.
  // Blocking on one verification request up front means logged-out (or
  // no-longer-logged-in) users never see so much as a flash of it.
  const [authChecked, setAuthChecked] = React.useState(false);

  React.useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      // A cached "signed in" user with no token can't be signed in: drop it, so
      // protected pages send the visitor to the login page (and back after).
      localStorage.removeItem(USER_KEY);
      setUser(loggedOutUser());
      setAuthChecked(true);
      return;
    }
    const fetchCurrentUser = (accessToken) =>
      axios.get("/api/users/current/", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    fetchCurrentUser(token)
      .catch((err) => {
        // Access tokens are short-lived: an expired one is normal, so renew
        // it from the refresh cookie and try once more.
        if (err.response && err.response.status === 401) {
          return refreshAccessToken().then(fetchCurrentUser);
        }
        throw err;
      })
      .then((res) => {
        const freshUser = buildUser(res.data, true);
        localStorage.setItem(USER_KEY, JSON.stringify(freshUser));
        setUser(freshUser);
      })
      .catch((err) => {
        // Offline or the server briefly unreachable: stay as we were rather
        // than signing everyone out. Only an answer from the server counts.
        if (!err.response) return;
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setUser(loggedOutUser());
      })
      .finally(() => setAuthChecked(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Running as the installed app counts as "installed" in the getting-started
  // checklist (the browser can't tell us the moment someone installs, only
  // that this is the installed app).
  React.useEffect(() => {
    if (user.isAuthenticated && isStandalone()) reportMilestone("installed");
  }, [user.isAuthenticated]);

  // axiosConfig.js signals this when the server says the session is over and
  // can't be renewed (or there is no token left). Going logged-out here makes
  // PrivateRoute redirect to /login from the current page and remember it.
  React.useEffect(() => {
    const onSignedOut = () => setUser(loggedOutUser());
    window.addEventListener(SIGNED_OUT_EVENT, onSignedOut);
    return () => window.removeEventListener(SIGNED_OUT_EVENT, onSignedOut);
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    // A mobile browser suspends network activity while the tab/PWA is
    // backgrounded, so this request can fail right as it wakes back up -
    // e.g. right after axiosConfig.js's expired-session redirect reloads
    // /login while connectivity is still coming back. Without a retry,
    // config (and therefore the SSO buttons on Login, which only render
    // once config is set - see socialApps below) stayed null until the
    // user manually reloaded the page.
    const fetchConfig = () => {
      document.removeEventListener("visibilitychange", retryIfVisible);
      window.removeEventListener("online", fetchConfig);
      axios
        .get("/api/config/")
        .then((res) => {
          if (!cancelled) setConfig(res.data);
        })
        .catch(() => {
          if (!cancelled) {
            document.addEventListener("visibilitychange", retryIfVisible);
            window.addEventListener("online", fetchConfig);
          }
        });
    };
    const retryIfVisible = () => {
      if (document.visibilityState === "visible") fetchConfig();
    };

    fetchConfig();

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", retryIfVisible);
      window.removeEventListener("online", fetchConfig);
    };
  }, []);

  return (
    <Router basename="/">
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        <InstallBanner />
        <Page>
          <ThemeContext.Provider value={themeContextProviderValue}>
            <UserContext.Provider value={userContextProviderValue}>
              <UpcomingEventsContext.Provider
                value={upcomingEventsContextProviderValue}
              >
                <SongProposalsContext.Provider
                  value={songProposalsContextProviderValue}
                >
                  <EmailConfirmationBanner />
                  <AppTour ready={authChecked} />
                  {!authChecked ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      minHeight="100vh"
                    >
                      <CircularProgress color="secondary" />
                    </Box>
                  ) : (
                    <Switch>
                      <Route path="/" exact>
                        <Home redirectIfAuthenticated />
                      </Route>
                      <Route path="/home" exact>
                        <Home />
                      </Route>
                      <Route path="/privacy_policy" exact>
                        <PrivacyPolicy />
                      </Route>
                      <Route path="/terms_of_service" exact>
                        <TermsOfService />
                      </Route>
                      <Route path="/login" exact>
                        <Login config={config} />
                      </Route>
                      <Route path="/not-invited" exact>
                        <NotInvited />
                      </Route>
                      <Route path="/email-in-use" exact>
                        <EmailInUse />
                      </Route>
                      <Route path="/verify-to-link" exact>
                        <VerifyToLink />
                      </Route>
                      <Route path="/confirm-erasure" exact>
                        <ConfirmErasure />
                      </Route>
                      <Route path="/verify-team-request" exact>
                        <VerifyTeamRequest />
                      </Route>
                      <Route path="/confirm-email/:key" exact>
                        <ConfirmEmail />
                      </Route>
                      <Route path="/signup">
                        <Signup config={config} />
                      </Route>
                      <Route path="/request-access" exact>
                        <RequestAccess />
                      </Route>
                      <Route
                        path="/password-reset/confirm/:id/:token"
                        component={PasswordReset}
                      />
                      <PrivateRoute path="/catalog" exact component={Catalog} />
                      <PrivateRoute
                        path="/song-proposals"
                        exact
                        component={SongProposals}
                      />
                      <PrivateRoute path="/calendar" exact component={Calendar} />
                      <PrivateRoute path="/upcoming" exact component={Upcoming} />
                      <PrivateRoute path="/events/:id" exact component={EventDetail} />
                      <PrivateRoute path="/songs/new" exact component={SongCreate} />
                      <PrivateRoute path="/share-target" exact component={ShareTarget} />
                      <PrivateRoute path="/songs/:id/edit" exact component={SongEdit} />
                      <PrivateRoute path="/songs/:id" exact component={SongDetail} />
                      <PrivateRoute path="/admin" exact component={Admin} />
                      <PrivateRoute path="/setlist-templates" exact component={SetlistTemplates} />
                      <PrivateRoute path="/account" exact component={AccountSettings} />
                      <PrivateRoute path="/" exact component={SongProposals} />
                      {/* Catches everything above didn't match - was
                          previously the "/" route's job (no `exact`,
                          so it silently matched ANY path), which meant
                          a typo'd URL like /song-propo rendered Song
                          Proposals instead of a real "page not found." */}
                      <Route component={NotFound} />
                    </Switch>
                  )}
                </SongProposalsContext.Provider>
              </UpcomingEventsContext.Provider>
            </UserContext.Provider>
          </ThemeContext.Provider>
        </Page>
      </MuiThemeProvider>
    </Router>
  );
}

export default App;
